import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { DebugLogger } from '@/utils/debugLogger';
import { isDemoMode, DEMO_USER, createDemoSession } from '@/utils/demoMode';
import type { UserRole, UserProfile } from './authContext';
import { fetchUserRole, fetchUserProfile } from './authDataService';

async function sendWelcomeEmailSilently(userId: string): Promise<void> {
  try {
    const { data: templates } = await supabase
      .from('email_templates')
      .select('id')
      .eq('name', 'welcome_sequence')
      .eq('is_active', true)
      .maybeSingle();
    if (!templates) return;
    await supabase.functions.invoke('email-manager', {
      body: {
        action: 'send',
        template_id: templates.id,
        variables: { user_name: '' },
      },
    });
  } catch {
    // Welcome email is non-blocking — never throw
  }
}

interface SignUpResult {
  user: User | null;
  session: Session | null;
  emailConfirmationRequired: boolean;
  error: any;
}

interface SignInResult {
  error: any;
}

interface AuthSetters {
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  setUserRole: (role: UserRole | null) => void;
  setProfile: (profile: UserProfile | null) => void;
  setLoading: (loading: boolean) => void;
  navigate: (path: string) => void;
}

/**
 * Handles user sign up with proper error handling and metadata.
 */
export async function signUp(
  email: string, 
  password: string, 
  role: UserRole = 'apprenant_particulier', 
  metadata?: Record<string, any>,
  setters?: AuthSetters
): Promise<SignUpResult> {
  // Demo mode bypass
  if (isDemoMode()) {
    console.log('🎭 Mode démo - inscription simulée');
    return {
      user: DEMO_USER as User,
      session: createDemoSession() as Session,
      emailConfirmationRequired: false,
      error: null,
    };
  }

  const redirectUrl = `${window.location.origin}/auth/confirm`;
  
  console.log('🚀 SignUp avec rôle:', role);
  
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: redirectUrl,
      data: {
        ...metadata,
        role: role,
        first_name: metadata?.first_name || '',
        last_name: metadata?.last_name || ''
      }
    }
  });

  if (error) {
    console.error('❌ SignUp error:', error);
    return handleSignUpError(error);
  }

  // Handle case where user already exists
  if (data?.user?.identities?.length === 0) {
    return { 
      user: null,
      session: null,
      emailConfirmationRequired: false,
      error: { 
        message: 'Un compte avec cette adresse email existe déjà. Veuillez vous connecter ou utiliser une autre adresse email.' 
      } 
    };
  }

  // If user is logged in immediately (email_confirm = OFF)
  if (data?.session && data?.user && setters) {
    console.log('🎯 Session établie immédiatement (email_confirm OFF)');
    setters.setSession(data.session);
    setters.setUser(data.user);

    // Fetch role and profile after a short delay
    setTimeout(async () => {
      const profileFromMetadata = {
        first_name: data.user.user_metadata?.first_name,
        last_name: data.user.user_metadata?.last_name
      };

      const [userRole, userProfile] = await Promise.all([
        fetchUserRole(data.user.id),
        fetchUserProfile(data.user.id)
      ]);
      setters.setUserRole(userRole);
      setters.setProfile(userProfile || profileFromMetadata);
      // Email de bienvenue (fire-and-forget)
      sendWelcomeEmailSilently(data.user.id);
    }, 500);
    
    return { 
      user: data.user, 
      session: data.session, 
      emailConfirmationRequired: false, 
      error: null 
    };
  }

  console.log('📧 Email de confirmation requis');
  return { 
    user: data?.user || null, 
    session: null, 
    emailConfirmationRequired: true, 
    error: null 
  };
}

function handleSignUpError(error: any): SignUpResult {
  const baseResult = { user: null, session: null, emailConfirmationRequired: false };
  
  if (error.message.includes('User already registered')) {
    return { 
      ...baseResult,
      error: { 
        ...error, 
        message: 'Un compte avec cette adresse email existe déjà. Veuillez vous connecter ou utiliser une autre adresse email.' 
      } 
    };
  }
  
  if (error.message.includes('Email not confirmed')) {
    return { 
      ...baseResult,
      error: { 
        ...error, 
        message: 'Votre adresse email doit être confirmée avant de pouvoir vous connecter. Vérifiez vos emails.' 
      } 
    };
  }
  
  if (error.message.includes('For security purposes, you can only request this after')) {
    return { 
      ...baseResult,
      error: { 
        ...error, 
        message: 'Trop de tentatives d\'inscription. Veuillez attendre quelques minutes avant de réessayer.' 
      } 
    };
  }
  
  return { ...baseResult, error };
}

/**
 * Handles user sign in with proper error handling.
 */
export async function signIn(
  email: string, 
  password: string,
  setters?: AuthSetters
): Promise<SignInResult> {
  // Demo mode bypass
  if (isDemoMode()) {
    console.log('🎭 Mode démo - connexion simulée');
    if (setters) {
      const demoSession = createDemoSession() as Session;
      setters.setSession(demoSession);
      setters.setUser(DEMO_USER as User);
      setters.setUserRole('admin_entreprise');
      setters.setProfile({ first_name: 'Compte', last_name: 'Démo' });
    }
    return { error: null };
  }

  try {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    
    if (error) {
      console.error('SignIn error:', error);
      return handleSignInError(error);
    }
    
    return { error: null };
  } catch (err: any) {
    console.error('SignIn network/unknown error:', err);
    return {
      error: {
        message: "Impossible de contacter le serveur d'authentification. Vérifiez votre connexion ou réessayez dans quelques instants.",
      },
    };
  }
}

function handleSignInError(error: any): SignInResult {
  if (error.message.includes('Email not confirmed')) {
    return { 
      error: { 
        ...error, 
        message: 'Vous devez confirmer votre adresse email avant de pouvoir vous connecter. Vérifiez votre boîte mail.' 
      } 
    };
  }
  
  if (error.message.includes('Invalid login credentials')) {
    return { 
      error: { 
        ...error, 
        message: 'Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.' 
      } 
    };
  }
  
  return { error };
}

/**
 * Handles user sign out.
 */
export async function signOut(setters: AuthSetters): Promise<void> {
  // Demo mode bypass
  if (isDemoMode()) {
    console.log('🎭 Mode démo - déconnexion simulée');
    setters.setUser(null);
    setters.setSession(null);
    setters.setUserRole(null);
    setters.setProfile(null);
    setters.setLoading(false);
    setters.navigate('/');
    return;
  }

  try {
    DebugLogger.log('auth', 'Starting sign out process');
    
    localStorage.removeItem('supabase.auth.token');
    sessionStorage.clear();
    
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      DebugLogger.log('error', 'Supabase signOut error', error);
    }
    
    setters.setUser(null);
    setters.setSession(null);
    setters.setUserRole(null);
    setters.setProfile(null);
    setters.setLoading(false);
    
    const isTestMode = window.location.pathname.includes('/dev-test-auth');
    if (!isTestMode) {
      DebugLogger.log('navigation', 'Redirecting to home after signOut');
      setters.navigate('/');
    }
  } catch (error) {
    DebugLogger.log('error', 'Exception during sign out', error);
    setters.setUser(null);
    setters.setSession(null);
    setters.setUserRole(null);
    setters.setProfile(null);
    setters.setLoading(false);
    
    const isTestMode = window.location.pathname.includes('/dev-test-auth');
    if (!isTestMode) {
      setters.navigate('/');
    }
  }
}
